/**
 * 会議タブを、既存の取組と同じ形の workstream に組み立てる。
 * 数字（現在地・予定）を持たないタブは「前回会議で出た課題・宿題・決定」だけを載せ、
 * 現在地は未設定として正直に出す（埋めない）。
 *
 * タブの表示名・紐づけ・どのタブを別扱いにするかは deploy 供給が持つ。
 */
import { useMemo } from 'react'

import { useMeetingTabs, type MeetingTab } from '@/components/BoardPreviousMeeting'
import { useBoardSections } from '@/hooks/useMeetingBoard'

interface PrevItem {
  id: string
  kind: 'action' | 'decision' | 'issue' | 'agenda'
  summary: string
  owner: string | null
  dueAt: string | null
}

interface PrevMeeting {
  tab: string
  sourceNote: string | null
  meetingTopic: string | null
  meetingStart: string | null
  items: PrevItem[]
}

/** 毎朝 Slack に流している日報。数字を持つ取組はこちらを現在地・予定にする */
interface DailyReport {
  state: 'delivered' | 'not_delivered'
  sourceLabel: string
  asOf?: string
  periodLabel?: string
  current?: string
  schedule?: string
  issues?: string[]
  highlights?: string[]
  alerts?: string[]
  note?: string
  plannedShape?: string[]
  footnote?: string
}

/**
 * 専用パネルを持つタブ（数字つきで別に組み立てているもの）はここでは扱わない。
 * どのタブがそれに当たるかは tabs 定義側の `renderedElsewhere` が決める。
 */
function extraTabs(tabs: MeetingTab[]): MeetingTab[] {
  return tabs.filter((tab) => tab.isExtraTab && tab.renderedElsewhere !== true)
}

/**
 * 会議タブを取組として snapshot に足す。
 * タブとして出すのは isExtraTab のものだけ（既存3取組は snapshot 側に既にある）。
 */
export function useSnapshotWithWorkstreams<T extends {
  workstreams: unknown[]
  decisions: unknown[]
  actionItems: unknown[]
}>(snapshot: T | undefined): T | undefined {
  const tabs = useMeetingTabs()
  const targets = extraTabs(tabs)
  const tabKeys = targets.map((tab) => tab.key)
  const results = useBoardSections<PrevMeeting>('previous', tabKeys)
  const dailyResults = useBoardSections<DailyReport>('daily', tabKeys)

  const loaded = results.map((r) => r.data)
  const dailies = dailyResults.map((r) => r.data)
  const signature = loaded.map((d) => `${d?.tab}:${d?.items.length}`).join('|')
    + '#' + dailies.map((d) => `${d?.state}:${d?.asOf ?? ''}`).join('|')

  return useMemo(() => {
    if (!snapshot || targets.length === 0) return snapshot
    const workstreams: unknown[] = []
    const decisions: unknown[] = []
    const actionItems: unknown[] = []

    targets.forEach((tab, index) => {
      const prev = loaded[index]
      const daily = dailies[index]
      const evidenceId = `meeting-${tab.key}`
      const dailyEvidenceId = `daily-${tab.key}`
      const observedAt = prev?.meetingStart ?? null
      const hasContent = Boolean(prev && prev.items.length > 0)
      const hasDaily = daily?.state === 'delivered'
      const evidenceRefs = [
        ...(hasContent ? [{
          evidenceId,
          state: 'verified' as const,
          sourceType: 'meeting',
          sourceObjectType: prev?.meetingTopic ?? '会議',
          observedAt,
          sourceUpdatedAt: observedAt,
        }] : []),
        ...(hasDaily ? [{
          evidenceId: dailyEvidenceId,
          state: 'verified' as const,
          sourceType: 'salesforce',
          sourceObjectType: daily?.sourceLabel ?? '日報',
          observedAt: daily?.asOf ?? null,
          sourceUpdatedAt: daily?.asOf ?? null,
        }] : []),
      ]
      const claim = (text: string) => hasContent
        ? { text, evidenceIds: [evidenceId], unknownReason: null }
        : { text: '', evidenceIds: [], unknownReason: 'evidence_unavailable' as const }
      const dailyClaim = (text: string | undefined) => hasDaily && text
        ? { text, evidenceIds: [dailyEvidenceId], unknownReason: null }
        : { text: '', evidenceIds: [], unknownReason: 'evidence_unavailable' as const }
      const unknown = { text: '', evidenceIds: [], unknownReason: 'evidence_unavailable' as const }
      const pick = (kind: PrevItem['kind']) => (prev?.items ?? []).filter((i) => i.kind === kind)

      // 数字は日報から、宿題・決定は会議から。両方あれば両方載せる
      const currentState = hasDaily
        ? dailyClaim([daily?.current, ...(daily?.highlights ?? [])].filter(Boolean).join(' ／ '))
        : hasContent
          ? claim(`前回会議（${prev?.meetingTopic ?? ''}）から 課題${pick('issue').length}件・宿題${pick('action').length}件・決定${pick('decision').length}件を取り込み済み。数字の取り込みはこれから`)
          : unknown
      const issues = [
        ...(daily?.issues ?? []).map((text) => dailyClaim(text)),
        ...pick('issue').map((i) => claim(i.summary)),
      ]

      workstreams.push({
        workstreamId: tab.key,
        title: tab.label ?? tab.key,
        ownerRef: null,
        ownerDisplayName: null,
        definitionOf100Percent: unknown,
        currentState,
        purpose: unknown,
        method: unknown,
        expectedResult: unknown,
        schedule: hasDaily ? dailyClaim(daily?.schedule) : unknown,
        issues,
        weeklyAgenda: pick('action').map((i) => claim(i.summary)),
        decisionRequests: pick('decision').map((i) => claim(i.summary)),
        scheduleState: 'unknown' as const,
        runtimeHealth: hasDaily
          ? { ...dailyClaim(`${daily?.sourceLabel}（${daily?.periodLabel ?? ''}）を取り込み済み`), state: 'healthy' as const }
          : hasContent
            ? { ...claim(`前回会議は取り込み済み`), state: 'healthy' as const }
            : { ...unknown, state: 'unknown' as const },
        handoffReadiness: { ...unknown, state: 'unknown' as const },
        outcomeGateSet: {
          approved: false, approvedBy: null, approvedAt: null, definitionHash: null,
          version: null, gates: [], percent: null,
        },
        evidenceRefs,
      })

      for (const item of pick('decision')) {
        decisions.push({
          decisionId: `${tab.key}-${item.id}`,
          summary: item.summary,
          state: 'proposed' as const,
          decidedAt: null,
          ownerRef: null,
          ownerDisplayName: null,
          evidenceIds: [evidenceId],
          unknownReason: null,
        })
      }
      for (const item of pick('action')) {
        actionItems.push({
          actionItemId: `${tab.key}-${item.id}`,
          summary: item.summary,
          ownerRef: item.owner ?? '',
          ownerDisplayName: item.owner,
          dueAt: item.dueAt,
          status: 'open' as const,
          evidenceIds: [evidenceId],
          unknownReason: null,
        })
      }
    })

    return {
      ...snapshot,
      workstreams: [...snapshot.workstreams, ...workstreams],
      decisions: [...snapshot.decisions, ...decisions],
      actionItems: [...snapshot.actionItems, ...actionItems],
    }
    // signature は取得済みデータが変わったことを表す（loaded は毎回新しい配列になるため）
  }, [snapshot, signature])
}
